import pricingData from "@/.Data/pricing_table.json";

export const BILLING_INTERVALS = ["monthly", "quarterly", "semiannual"] as const;
export const JOBOLAR_PLAN_IDS = ["basic", "standard", "pro"] as const;
export const INTERVIEW_COPILOT_ONLY_PLAN_ID = "interview-copilot-only" as const;
export const CHECKOUT_PLAN_IDS = [
  ...JOBOLAR_PLAN_IDS,
  INTERVIEW_COPILOT_ONLY_PLAN_ID,
] as const;

export type BillingInterval = (typeof BILLING_INTERVALS)[number];
export type JobolarPlanId = (typeof JOBOLAR_PLAN_IDS)[number];
export type CheckoutPlanId = (typeof CHECKOUT_PLAN_IDS)[number];

export const PROMOTION_PERCENT = pricingData.promoPercent;
export const PRICING_DATA = pricingData;

export function isBillingInterval(value: unknown): value is BillingInterval {
  return BILLING_INTERVALS.includes(value as BillingInterval);
}

export function isCheckoutPlanId(value: unknown): value is CheckoutPlanId {
  return CHECKOUT_PLAN_IDS.includes(value as CheckoutPlanId);
}

function cents(value: number): number {
  return Math.round((value + Number.EPSILON) * 100);
}

function dollars(valueInCents: number): number {
  return valueInCents / 100;
}

function hasExactPromotion(regularCents: number, promoCents: number): boolean {
  return promoCents * 100 === regularCents * (100 - PROMOTION_PERCENT);
}

export interface CanonicalCheckoutSelection {
  planId: CheckoutPlanId;
  interval: BillingInterval;
  termMonths: number;
  regularAmountUsd: number;
  amountUsd: number;
  regularAmountCents: number;
  amountCents: number;
  stripeUnitAmountCents: number;
  stripeQuantity: number;
  interviewHoursPerMonth?: number;
  interviewMonthlyLimitMinutes?: number;
  prepaidTermHours?: number;
}

export function getCanonicalCheckoutSelection(
  planIdValue: unknown,
  intervalValue: unknown,
  interviewHoursValue?: unknown,
): CanonicalCheckoutSelection {
  if (!isCheckoutPlanId(planIdValue)) throw new Error("invalid_plan");
  if (!isBillingInterval(intervalValue)) throw new Error("invalid_interval");

  const planId = planIdValue;
  const interval = intervalValue;
  const termMonths = pricingData.billingIntervals[interval].months;

  if (planId !== INTERVIEW_COPILOT_ONLY_PLAN_ID) {
    const regularAmountCents = cents(pricingData.anchorPrices[planId][interval]);
    const amountCents = cents(pricingData.promoPrices[planId][interval]);
    if (!hasExactPromotion(regularAmountCents, amountCents)) {
      throw new Error(`invalid_discount_configuration:${planId}:${interval}`);
    }
    return {
      planId,
      interval,
      termMonths,
      regularAmountUsd: dollars(regularAmountCents),
      amountUsd: dollars(amountCents),
      regularAmountCents,
      amountCents,
      // Stripe stores the regular amount. Checkout applies the configured
      // automatic 60%-off coupon, while Flutterwave stores promo amounts.
      stripeUnitAmountCents: regularAmountCents,
      stripeQuantity: 1,
    };
  }

  const hours = Number(interviewHoursValue);
  const config = pricingData.interviewCopilotOnly;
  if (
    !Number.isInteger(hours) ||
    hours < config.minimumHours ||
    hours > config.maximumHours
  ) {
    throw new Error("invalid_interview_hours");
  }

  const unit = config.unitPrices[interval];
  const regularUnitCents = cents(unit.regular);
  const promoUnitCents = cents(unit.promo);
  if (!hasExactPromotion(regularUnitCents, promoUnitCents)) {
    throw new Error(`invalid_discount_configuration:${planId}:${interval}`);
  }
  const regularAmountCents = regularUnitCents * hours;
  const amountCents = promoUnitCents * hours;

  return {
    planId,
    interval,
    termMonths,
    regularAmountUsd: dollars(regularAmountCents),
    amountUsd: dollars(amountCents),
    regularAmountCents,
    amountCents,
    stripeUnitAmountCents: regularUnitCents,
    stripeQuantity: hours,
    interviewHoursPerMonth: hours,
    interviewMonthlyLimitMinutes: hours * 60,
    prepaidTermHours: hours * termMonths,
  };
}

export function allCanonicalCheckoutSelections(): CanonicalCheckoutSelection[] {
  return CHECKOUT_PLAN_IDS.flatMap((planId) =>
    BILLING_INTERVALS.map((interval) =>
      getCanonicalCheckoutSelection(planId, interval, 1),
    ),
  );
}
